import clsx from 'clsx';

import './participantsSection.scss';
import {
  ParticipantTile,
  TrackLoop,
  useIsMuted,
  useIsSpeaking,
  useLocalParticipant,
  useParticipantAttribute,
  useParticipantContext,
  useRemoteParticipants,
  useTracks
} from '@livekit/components-react';
import api from '@/api/api';
import { Track } from 'livekit-client';
import { useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { ParticipantRole } from '@/consts/meeting.ts';
import { useUserStore } from '@/stores/user.ts';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useSend } from '@/components/SendProvider';
import { useStudyModeStore } from '@/stores/studyMode.ts';
import { motion, AnimatePresence } from 'framer-motion';

type Room = {
  id: number;
  title: string;
  isEditing?: boolean;
};

type CreateOrEditRoomMessage = {
  action: 'create-room' | 'edit-room';
  newRoom: Room;
};

type TransferToCheckRoomMessage = {
  action: string;
  studentId: string;
  roomId: number;
};

interface ParticipantsSectionProps {
  sectionTitle: string;
  role: string;
  rooms?: Room[];
  onViewBoard?: (userUid: string | null) => void;
}

interface ParticipantsListProps {
  role?: string;
  roomId?: number;
  onViewBoard?: (userUid: string | null) => void;
  rooms?: Room[];
  busyRooms?: Set<number>;
}

function ParticipantListItem({ role, roomId, rooms, busyRooms }: ParticipantsListProps) {
  const participant = useParticipantContext();
  const isSpeaking = useIsSpeaking();
  const isMuted = useIsMuted({ source: Track.Source.Microphone, participant });
  const roleAttr = useParticipantAttribute('role', { participant });
  const roomIdAttr = useParticipantAttribute('roomId', { participant });
  const { localParticipant } = useLocalParticipant();

  const userUid = useUserStore((state) => state.userUid);
  const userRole = useUserStore((state) => state.role);
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);

  const currentlyViewingUserUid = useMeetingStore((state) => state.currentlyViewingUserUid);
  const handUpStudents = useMeetingStore((state) => state.handUpStudents);
  const meeting = useMeetingStore((state) => state.meeting);

  const { setCurrentlyViewingUserUid, addRoom, setHandUpStudents } = useMeetingStore.getState();
  const { sendTransferToCheckRoom, sendCreateOrEditRoom } = useSend();

  const shouldShowParticipant = roomId
    ? parseInt(roomIdAttr) == roomId
    : role
      ? roleAttr === role && !roomIdAttr
      : false;

  const handleViewBoardClick = () => {
    if (setCurrentlyViewingUserUid) {
      setCurrentlyViewingUserUid(participant.identity);
    }
  };

  const transferToRoomClick = async () => {
    let roomForCheckId;
    if (rooms) {
      for (const room of rooms) {
        if (!busyRooms?.has(room.id)) {
          roomForCheckId = room.id;
          break;
        }
      }
    }
    if (!roomForCheckId) {
      if (!meeting?.uuid || !rooms) return;
      try {
        const newTitle = `Комната ${rooms.length + 1}`;
        const meetingRoom = await api.createRoom(meeting?.uuid, { name: newTitle });
        const newRoom = addRoom(meetingRoom.id, newTitle);
        roomForCheckId = newRoom.id;
        if (sendCreateOrEditRoom) {
          const message: CreateOrEditRoomMessage = {
            action: 'create-room',
            newRoom: newRoom
          };
          sendCreateOrEditRoom(new TextEncoder().encode(JSON.stringify(message)), {
            reliable: true
          });
        }
      } catch (err) {
        console.error('Error creating room', err);
      }
    }
    if (roomForCheckId) {
      await localParticipant.setAttributes({ roomId: roomForCheckId.toString() });

      if (handUpStudents?.includes(participant.identity)) {
        setHandUpStudents((prevStudents) =>
          prevStudents.filter((student) => student !== participant.identity)
        );
      }

      const message: TransferToCheckRoomMessage = {
        action: 'transfer-student-to-check-room',
        studentId: participant.identity,
        roomId: roomForCheckId
      };
      if (sendTransferToCheckRoom) {
        sendTransferToCheckRoom(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true
        });
      }
    }
  };

  if (!shouldShowParticipant) {
    return;
  }

  return (
    <div className="participant">
      {handUpStudents?.includes(participant.identity) && role === ParticipantRole.STUDENT ? (
        userRole !== ParticipantRole.STUDENT ? (
          <span
            className={clsx('participant__hand-up active')}
            onClick={() => transferToRoomClick()}
          >
            <div className={clsx('participant__hand-up_block active')}>
              <div className={clsx('participant__hand-up_label active')}>
                Помочь: {handUpStudents.indexOf(participant.identity) + 1}
              </div>
              <div className="participant__hand-up_name">{participant.name}</div>
            </div>
          </span>
        ) : (
          <span className={clsx('participant__hand-up students active')}>
            <div className={clsx('participant__hand-up_block students active')}>
              <div className={clsx('participant__hand-up_label active')}>
                В очереди: {handUpStudents.indexOf(participant.identity) + 1}
              </div>
              <div className="participant__hand-up_name">{participant.name}</div>
            </div>
          </span>
        )
      ) : (
        <span className="participant__name">
          <>{participant.name}</>
        </span>
      )}
      <div className="participant__right">
        {currentlyViewingUserUid && participant.identity === userUid && (
          <Button
            className="participant__return-button"
            icon="pi pi-arrow-circle-left"
            size="small"
            label="к своей доске"
            link
            onClick={() => setCurrentlyViewingUserUid(null)}
          />
        )}
        <div className="participant__status">
          {userRole !== ParticipantRole.STUDENT &&
            roleAttr === ParticipantRole.STUDENT &&
            isPracticeMode && (
              <i
                className={clsx(
                  'pi pi-eye participant__icon participant__icon_view',
                  participant.identity === currentlyViewingUserUid && 'participant__icon_active'
                )}
                title="Посмотреть доску ученика"
                onClick={handleViewBoardClick}
              />
            )}
          <i
            className={clsx(
              'pi',
              'pi-microphone',
              'participant__icon',
              isSpeaking && 'participant__icon_speaking',
              isMuted && 'participant__icon_muted'
            )}
          ></i>
        </div>
      </div>
    </div>
  );
}

export function ParticipantsList(props: ParticipantsListProps) {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const handUpStudents = useMeetingStore((state) => state.handUpStudents);
  useEffect(() => {
    const localRoomId = localParticipant?.attributes?.roomId;

    remoteParticipants.forEach((participant) => {
      const participantRoomId = participant?.attributes?.roomId;

      const shouldSubscribe =
        (localRoomId && participantRoomId === localRoomId) || (!localRoomId && !participantRoomId);

      participant.trackPublications.forEach((trackPublication) => {
        trackPublication.setSubscribed(shouldSubscribe);
      });
    });
  }, [props.roomId, localParticipant, remoteParticipants]);

  const tracks = useTracks([{ source: Track.Source.Microphone, withPlaceholder: false }], {
    onlySubscribed: false
  });

  const uniqueBusyRooms = useMemo(() => {
    return new Set(remoteParticipants.map((p) => Number(p.attributes?.roomId)));
  }, [remoteParticipants, props.rooms]);

  const sortedTracks = useMemo(() => {
    return [...tracks].sort((a, b) => {
      const idA = a.participant?.identity ?? '';
      const idB = b.participant?.identity ?? '';

      const indexA = handUpStudents.indexOf(idA);
      const indexB = handUpStudents.indexOf(idB);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      const nameA = a.participant?.name ?? '';
      const nameB = b.participant?.name ?? '';
      return nameA.localeCompare(nameB);
    });
  }, [tracks, handUpStudents]);

  return (
    <div className="participants__list">
      <TrackLoop tracks={sortedTracks}>
        <AnimatePresence>
          <motion.div
            layout
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1.0 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <ParticipantTile>
              <ParticipantListItem {...props} busyRooms={uniqueBusyRooms} />
            </ParticipantTile>
          </motion.div>
        </AnimatePresence>
      </TrackLoop>
    </div>
  );
}

function ParticipantsSection({ sectionTitle, role, rooms }: ParticipantsSectionProps) {
  return (
    <div className="participants__section">
      <p className="participants__section-title">
        <span>{sectionTitle}</span>
      </p>
      <ParticipantsList role={role} rooms={rooms} />
    </div>
  );
}

export default ParticipantsSection;
